(() => {
  if (document.documentElement.hasAttribute('data-scp-smart-sticky')) return;
  document.documentElement.setAttribute('data-scp-smart-sticky', '');
  const layouts = new Map();
  const desktop = matchMedia('(min-width: 761px)');
  let frame;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      layouts.forEach((state, layout) => {
        const { column, bar } = state;
        const y = Math.max(0, window.scrollY);
        if (document.fullscreenElement) {
          state.lastY = y;
          return;
        }
        if (!desktop.matches) {
          delete column.dataset.scpSmartSticky;
          column.style.removeProperty('--scp-smart-top');
          state.offset = undefined;
          state.lastY = y;
          return;
        }
        const viewport = document.documentElement.clientHeight;
        const header = document.querySelector('header.site-header');
        const top = Math.max(0, header?.getBoundingClientRect().bottom || 0) + 16;
        const barBox = bar?.getBoundingClientRect();
        const bottom = barBox?.height && barBox.top < viewport ? barBox.top - 12 : viewport - 20;
        const lower = Math.min(top, bottom - column.getBoundingClientRect().height);
        const next = state.offset === undefined ? layout.getBoundingClientRect().top : state.offset - (y - state.lastY);
        state.offset = Math.min(top, Math.max(lower, next));
        state.lastY = y;
        column.dataset.scpSmartSticky = '';
        const offset = Math.round(state.offset * 100) / 100 + 'px';
        if (column.style.getPropertyValue('--scp-smart-top') !== offset) column.style.setProperty('--scp-smart-top', offset);
      });
    });
  };
  const bind = layout => {
    if (layouts.has(layout)) return;
    const root = layout.closest('scd-wheel-builder');
    const column = layout.querySelector('.scdwb-gallery-column');
    if (!root || !column) return;
    const bar = root.querySelector('.scdwb-current-selections');
    const events = new AbortController();
    const viewer = column.querySelector('[data-scdwb-live-model]');
    viewer?.addEventListener('wheel', event => {
      if (desktop.matches && !document.fullscreenElement && !event.ctrlKey && !event.metaKey) event.stopPropagation();
    }, { capture: true, passive: true, signal: events.signal });
    const observer = new ResizeObserver(schedule);
    [column, bar, document.querySelector('header.site-header')].filter(Boolean).forEach(element => observer.observe(element));
    layouts.set(layout, { column, bar, events, observer, lastY: Math.max(0, window.scrollY) });
    schedule();
  };
  const scan = node => {
    if (node.matches?.('.scdwb-builder-layout')) bind(node);
    node.querySelectorAll?.('.scdwb-builder-layout').forEach(bind);
  };
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) scan(node);
    for (const [layout, state] of layouts) if (!layout.isConnected) {
      state.events.abort();
      state.observer.disconnect();
      layouts.delete(layout);
    }
    schedule();
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('scroll', schedule, { passive: true });
  window.visualViewport?.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('fullscreenchange', schedule);
  desktop.addEventListener('change', schedule);
  scan(document);
})();

(() => {
  if (customElements.get('scp-wheel-viewer-help')) return;
  customElements.define('scp-wheel-viewer-help', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const trigger = this.querySelector('[data-viewer-help-trigger]');
      const tip = this.querySelector('[data-viewer-help-tip]');
      if (!trigger || !tip) return;
      let pinned = false;
      const show = open => {
        tip.hidden = !open;
        trigger.setAttribute('aria-expanded', String(open));
      };
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch') show(true);
      }, options);
      this.addEventListener('pointerleave', () => {
        if (!pinned && !this.contains(document.activeElement)) show(false);
      }, options);
      this.addEventListener('focusin', () => show(true), options);
      this.addEventListener('focusout', event => {
        if (!pinned && !this.contains(event.relatedTarget)) show(false);
      }, options);
      trigger.addEventListener('click', () => { pinned = !pinned; show(pinned); }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        pinned = false;
        show(false);
      }, options);
      document.addEventListener('pointerdown', event => {
        if (this.contains(event.target)) return;
        pinned = false;
        show(false);
      }, options);
    }
    disconnectedCallback() { this.events?.abort(); }
  });
})();

(() => {
  if (customElements.get('scp-wheel-model-selector')) return;

  const loadViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!window.scpWheelModelViewerPromise) {
      window.scpWheelModelViewerPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.3.1/model-viewer.min.js';
        script.onload = () => customElements.whenDefined('model-viewer').then(resolve, reject);
        script.onerror = () => reject(new Error('Model viewer unavailable'));
        document.head.appendChild(script);
      }).catch(error => {
        delete window.scpWheelModelViewerPromise;
        throw error;
      });
    }
    return window.scpWheelModelViewerPromise;
  };

  customElements.define('scp-wheel-model-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.resetPreview();
        return;
      }
      this.initialized = true;
      this.revision = 0;
      this.choices = [...this.querySelectorAll('[data-wheel-choice]')];
      this.stage = this.querySelector('[data-wheel-stage]');
      this.poster = this.querySelector('[data-wheel-poster]');
      this.video = this.querySelector('[data-scp-wheel-loop]');
      this.status = this.querySelector('[data-wheel-status]');
      this.activate = this.querySelector('[data-wheel-activate]');
      this.activateLabel = this.querySelector('[data-wheel-activate-label]');
      this.hint = this.querySelector('[data-wheel-hint]');
      this.details = this.querySelector('[data-wheel-selected]');
      this.builds = this.closest('section').querySelector('[data-wheel-builds]');
      this.selected = null;
      this.choices.forEach(button => button.addEventListener('click', () => this.select(button)));
      this.activate.addEventListener('click', () => this.loadSelected());
    }

    disconnectedCallback() {
      this.revision++;
      this.releaseViewer();
    }

    releaseViewer() {
      if (!this.viewer) return;
      this.viewer.removeAttribute('src');
      this.viewer.remove();
      this.viewer = null;
    }

    resetPreview() {
      this.revision++;
      this.releaseViewer();
      this.loading = false;
      this.classList.remove('is-ready');
      this.stage.setAttribute('aria-busy', 'false');
      this.status.textContent = '';
      this.activate.hidden = false;
      if (this.video) this.video.hidden = false;
      this.activate.disabled = false;
      this.activateLabel.textContent = this.dataset.loadLabel;
      this.hint.textContent = this.dataset.photoHint;
    }

    select(button) {
      if (button === this.selected) return;
      this.selected = button;
      if (this.video) this.video.dataset.scpVideoProfile = button.dataset.wheelModel;
      this.resetPreview();
      this.choices.forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
      this.querySelector('[data-wheel-name]').textContent = button.dataset.name;
      this.querySelector('[data-wheel-description]').textContent = button.dataset.description;
      this.querySelector('[data-wheel-price]').textContent = button.dataset.price;
      this.querySelector('[data-wheel-customize]').href = button.dataset.url;
      this.poster.src = button.dataset.poster;
      this.poster.alt = button.dataset.name;
      this.details.hidden = false;
      this.builds.hidden = ![...this.builds.querySelectorAll('[data-wheel-model]')].some(card => card.dataset.wheelModel === button.dataset.wheelModel);
      this.closest('section').dispatchEvent(new CustomEvent('scp:wheel-selected', { detail: { model: button.dataset.wheelModel, name: button.dataset.name } }));
    }

    async loadSelected() {
      if (!this.selected || this.loading) return;
      const choice = this.selected;
      const revision = ++this.revision;
      this.releaseViewer();
      this.loading = true;
      this.activate.disabled = true;
      this.activateLabel.textContent = this.dataset.loading;
      this.stage.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      try {
        await loadViewer();
        if (!this.isConnected || revision !== this.revision || choice !== this.selected) return;
        const viewer = document.createElement('model-viewer');
        this.viewer = viewer;
        const current = () => this.isConnected && revision === this.revision && this.viewer === viewer && this.selected === choice;
        const attributes = {
          'data-wheel-preview': '', alt: choice.dataset.name, 'camera-controls': '',
          'camera-orbit': choice.dataset.orbit, 'field-of-view': '30deg',
          'min-field-of-view': '30deg', 'max-field-of-view': '30deg',
          exposure: '1.2', 'shadow-intensity': '.45', 'interaction-prompt': 'none',
          'touch-action': 'pan-y', tabindex: '0'
        };
        Object.entries(attributes).forEach(([name, value]) => viewer.setAttribute(name, value));
        viewer.addEventListener('load', async () => {
          if (!current()) return;
          try {
            if (choice.dataset.orbit.startsWith('180deg')) await this.applyLciFinish(viewer);
            else if (choice.dataset.wheelModel === 'pre-lci') await this.applyPreLciFinish(viewer);
          } catch {
            if (current()) this.showError();
            return;
          }
          if (!current()) return;
          viewer.setAttribute('camera-orbit', choice.dataset.orbit);
          requestAnimationFrame(() => { if (current()) viewer.jumpCameraToGoal(); });
          this.classList.add('is-ready');
          if (this.video) { this.video.pause(); this.video.hidden = true; }
          this.loading = false;
          this.activate.hidden = true;
          this.stage.setAttribute('aria-busy', 'false');
          this.status.textContent = '';
          this.hint.textContent = this.dataset.modelHint;
        }, { once: true });
        viewer.addEventListener('error', () => { if (current()) this.showError(); });
        this.querySelector('[data-wheel-canvas]').appendChild(viewer);
        // This is the only place a GLB source is assigned: an explicit Load 3D click.
        viewer.setAttribute('src', choice.dataset.modelUrl);
      } catch {
        if (revision === this.revision && choice === this.selected) this.showError();
      }
    }

    showError() {
      this.releaseViewer();
      this.loading = false;
      this.classList.remove('is-ready');
      this.stage.setAttribute('aria-busy', 'false');
      this.status.textContent = this.dataset.error;
      this.activate.hidden = false;
      if (this.video) this.video.hidden = false;
      this.activate.disabled = false;
      this.activateLabel.textContent = this.dataset.retryLabel;
    }

    async applyPreLciFinish(viewer) {
      const model = viewer.model;
      if (!model) return;
      const [carbon, grip] = await Promise.all([
        viewer.createTexture('https://cdn.shopify.com/s/files/1/0780/0356/8812/files/shop-carbon-parts-wheel-black-twill-original-weave-20260924.png?v=1790245261'),
        viewer.createTexture('https://cdn.shopify.com/s/files/1/0780/0356/8812/files/shop-carbon-parts-wheel-final-material-perf-0-20260923.png?v=1790232078')
      ]);
      if (this.viewer !== viewer || viewer.model !== model) return;
      for (const texture of [carbon, grip]) {
        texture.sampler.setWrapS(10497);
        texture.sampler.setWrapT(10497);
        texture.sampler.setScale({ u: 1, v: 1 });
      }
      for (const name of [
        'Blue-flake forged carbon - supplied swatch',
        'Paddle shifters - fixed black carbon',
        'Configurable middle trim shell',
        'Middle button surrounds - configurable finish'
      ]) {
        const material = model.getMaterialByName(name);
        if (!material) throw new Error('Missing carbon preview material');
        const pbr = material.pbrMetallicRoughness;
        pbr.baseColorTexture.setTexture(carbon);
        pbr.setBaseColorFactor('#ffffff');
        pbr.setMetallicFactor(.04);
        pbr.setRoughnessFactor(.22);
        material.normalTexture?.setTexture(null);
        material.emissiveTexture?.setTexture(null);
        material.setEmissiveFactor?.([0, 0, 0]);
      }
      const leather = model.getMaterialByName('Grey Alcantara - supplied swatch');
      if (leather) {
        leather.pbrMetallicRoughness.baseColorTexture.setTexture(grip);
        leather.pbrMetallicRoughness.setBaseColorFactor('#ffffff');
        leather.pbrMetallicRoughness.setMetallicFactor(0);
        leather.pbrMetallicRoughness.setRoughnessFactor(.58);
      }
      const cover = model.getMaterialByName('Configurable airbag cover');
      if (cover) {
        cover.pbrMetallicRoughness.baseColorTexture.setTexture(null);
        cover.pbrMetallicRoughness.setBaseColorFactor('#17191b');
        cover.pbrMetallicRoughness.setRoughnessFactor(.65);
      }
      const insert = model.getMaterialByName('Configurable no-hole center insert');
      if (insert) {
        insert.setAlphaMode('BLEND');
        insert.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 0]);
      }
      for (const name of [
        'BMW tri-color thread - light blue', 'BMW tri-color thread - dark blue', 'BMW tri-color thread - red',
        'Configurable airbag stitch outer', 'Configurable airbag stitch middle', 'Configurable airbag stitch inner'
      ]) {
        model.getMaterialByName(name)?.pbrMetallicRoughness.setBaseColorFactor('#202126');
      }
    }

    async applyLciFinish(viewer) {
      const model = viewer.model;
      if (!model) return;
      const parts = name => model.materials.filter(material => material.name === name || material.name.startsWith(name + '.'));
      // Match the builder's neutral leather cover and grips, not the GLB's placeholder carbon.
      for (const name of ['ADRO grip leather', 'ADRO airbag cover']) {
        for (const material of parts(name)) {
          const pbr = material.pbrMetallicRoughness;
          pbr.baseColorTexture.setTexture(null);
          pbr.setBaseColorFactor('#17181a');
          pbr.setMetallicFactor(0);
          pbr.setRoughnessFactor(.75);
          material.setClearcoatFactor?.(0);
        }
      }
      try {
        const urls = [
          'https://cdn.shopify.com/s/files/1/0780/0356/8812/files/shop-carbon-parts-wheel-black-twill-original-weave-20260924.png?v=1790245261',
          'https://cdn.shopify.com/s/files/1/0780/0356/8812/files/shop-carbon-parts-wheel-final-material-perf-0-20260923.png?v=1790232078',
          'https://cdn.shopify.com/s/files/1/0780/0356/8812/files/shop-carbon-parts-wheel-final-material-smooth-0-20260923.png?v=1790231849'
        ];
        const textures = await Promise.all(urls.map(url => viewer.createTexture(url)));
        if (this.viewer !== viewer || viewer.model !== model) return;
        textures.forEach(texture => {
          texture.sampler.setWrapS(10497);
          texture.sampler.setWrapT(10497);
        });
        for (const [name, index, glossy] of [
          ['ADRO rim carbon', 0, true], ['ADRO button surround carbon', 0, true],
          ['ADRO middle trim shell', 0, true], ['ADRO grip leather', 1, false], ['ADRO airbag cover', 2, false]
        ]) {
          for (const material of parts(name)) {
            const pbr = material.pbrMetallicRoughness;
            pbr.baseColorTexture.setTexture(textures[index]);
            pbr.setBaseColorFactor('#ffffff');
            pbr.setMetallicFactor(glossy ? .035 : 0);
            pbr.setRoughnessFactor(glossy ? .17 : .75);
            material.setClearcoatFactor?.(glossy ? 1 : 0);
            material.setClearcoatRoughnessFactor?.(.075);
            if (glossy) material.normalTexture?.setTexture(null);
          }
        }
        for (let i = 0; i < 3; i++) {
          for (const material of parts('ADRO top stripe ' + i)) {
            const pbr = material.pbrMetallicRoughness;
            pbr.baseColorTexture.setTexture(null);
            pbr.setBaseColorFactor(['#d12736', '#153c89', '#48a5de'][i]);
            pbr.setRoughnessFactor(.17);
            material.normalTexture?.setTexture(null);
          }
        }
        textures.forEach((texture, index) => texture.sampler.setScale({ u: index < 2 ? 4 : 1, v: index < 2 ? 4 : 1 }));
      } catch {
        // Neutral matte leather remains usable if a texture is unavailable.
      }
    }
  });
})();

(() => {
  if (document.documentElement.hasAttribute('data-scp-preview-motion')) return;
  document.documentElement.setAttribute('data-scp-preview-motion', '');
  const selector = 'model-viewer[data-scdwb-live-model], scp-wheel-model-selector model-viewer[data-wheel-preview]';
  const previews = new Map();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const cursorIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20" fill="#202329" stroke="#fff" stroke-width="1.5"/><g fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17a14 14 0 0 1 24-3m0-5v5h-5M34 27a14 14 0 0 1-24 3m0 5v-5h5"/><path d="M15 22h14m-11-3-3 3 3 3m8-6 3 3-3 3"/></g></svg>';
  const cursor = `url("data:image/svg+xml,${encodeURIComponent(cursorIcon)}") 22 22, grab`;
  const sync = (viewer, state) => {
    const spinning = !state.interacted && !viewer.hasAttribute('data-scp-video-idle') && !reducedMotion.matches && !document.hidden && state.visible;
    viewer.toggleAttribute('auto-rotate', spinning);
    viewer.dataset.scpIdleSpin = state.interacted ? 'stopped' : spinning ? 'rotating' : 'paused';
  };
  const intersections = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = previews.get(entry.target);
      if (!state) continue;
      state.visible = entry.isIntersecting;
      sync(entry.target, state);
    }
  }, { threshold: 0.01 }) : null;
  const bind = viewer => {
    if (previews.has(viewer)) return;
    const events = new AbortController();
    const state = { interacted: false, visible: !intersections, events };
    previews.set(viewer, state);
    viewer.setAttribute('rotation-per-second', '18deg');
    viewer.setAttribute('auto-rotate-delay', '1600');
    viewer.style.cursor = cursor;
    const stop = () => {
      state.interacted = true;
      sync(viewer, state);
    };
    const release = () => { viewer.style.cursor = cursor; };
    viewer.addEventListener('pointerdown', event => {
      stop();
      if (event.pointerType !== 'touch') viewer.style.cursor = 'grabbing';
    }, { capture: true, passive: true, signal: events.signal });
    viewer.addEventListener('click', stop, { capture: true, signal: events.signal });
    viewer.addEventListener('wheel', stop, { capture: true, passive: true, signal: events.signal });
    viewer.addEventListener('keydown', event => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Enter', ' '].includes(event.key)) stop();
    }, { capture: true, signal: events.signal });
    viewer.addEventListener('camera-change', event => {
      if (event.detail?.source === 'user-interaction') stop();
    }, { signal: events.signal });
    viewer.addEventListener('load', () => sync(viewer, state), { signal: events.signal });
    viewer.addEventListener('scp:preview-activate', () => sync(viewer, state), { signal: events.signal });
    window.addEventListener('pointerup', release, { passive: true, signal: events.signal });
    window.addEventListener('pointercancel', release, { passive: true, signal: events.signal });
    window.addEventListener('blur', release, { signal: events.signal });
    customElements.whenDefined('model-viewer').then(() => {
      if (!viewer.isConnected || events.signal.aborted || !viewer.shadowRoot) return;
      if (!viewer.shadowRoot.querySelector('[data-scp-spin-cursor]')) {
        const style = document.createElement('style');
        style.setAttribute('data-scp-spin-cursor', '');
        style.textContent = ':host([data-scp-idle-spin]) .userInput{cursor:inherit!important}';
        viewer.shadowRoot.appendChild(style);
      }
    });
    intersections?.observe(viewer);
    sync(viewer, state);
  };
  const scan = root => {
    if (root.matches?.(selector)) bind(root);
    root.querySelectorAll?.(selector).forEach(bind);
  };
  const refresh = () => previews.forEach((state, viewer) => sync(viewer, state));
  reducedMotion.addEventListener('change', refresh);
  document.addEventListener('visibilitychange', refresh);
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) scan(node);
    for (const [viewer, state] of previews) {
      if (viewer.isConnected) continue;
      state.events.abort();
      intersections?.unobserve(viewer);
      previews.delete(viewer);
    }
  }).observe(document.body, { childList: true, subtree: true });
  scan(document);
})();

(() => {
  if (document.documentElement.hasAttribute('data-scp-preview-video')) return;
  document.documentElement.setAttribute('data-scp-preview-video', '');
  const sources = {
    'f-chassis': 'https://cdn.shopify.com/videos/c/o/v/9da783636f6a478889125eb3a4d5f1d0.mp4',
    'pre-lci': 'https://cdn.shopify.com/videos/c/o/v/6b15db71efe54a129400415db356f222.mp4',
    'lci-2025': 'https://cdn.shopify.com/videos/c/o/v/dd95f824a3b04cb696a811e256e81947.mp4',
    'adro-lci': 'https://cdn.shopify.com/videos/c/o/v/dd95f824a3b04cb696a811e256e81947.mp4'
  };
  const videos = new Map();
  const products = new WeakSet();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const label = (video, state) => {
    if (!state.toggle) return;
    const paused = video.paused;
    state.toggle.textContent = paused ? state.toggle.dataset.scpPlayLabel : state.toggle.dataset.scpPauseLabel;
    state.toggle.setAttribute('aria-pressed', String(paused));
  };
  const play = (video, state) => {
    const src = sources[video.dataset.scpVideoProfile];
    const active = state.visible && !video.hidden && !document.hidden && !state.paused && (!motion.matches || state.manual);
    if (!active || !src?.startsWith('https://')) { video.pause(); label(video, state); return; }
    if (video.getAttribute('src') !== src) {
      video.classList.remove('is-loaded');
      video.src = src;
      video.load();
    }
    video.muted = true;
    video.play().then(() => label(video, state)).catch(() => label(video, state));
  };
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = videos.get(entry.target);
      if (!state) continue;
      state.visible = entry.isIntersecting;
      play(entry.target, state);
    }
  }, {threshold: 0.01});
  const bindVideo = video => {
    if (videos.has(video)) return;
    const toggle = video.parentElement.querySelector('[data-scp-video-toggle]');
    const state = {visible: false, paused: false, manual: false, toggle};
    videos.set(video, state);
    video.muted = true;
    video.addEventListener('loadeddata', () => video.classList.add('is-loaded'));
    video.addEventListener('error', () => video.classList.remove('is-loaded'));
    for (const event of ['play', 'pause', 'ended']) video.addEventListener(event, () => label(video, state));
    toggle?.addEventListener('click', () => {
      state.paused = !video.paused;
      state.manual = true;
      play(video, state);
    });
    observer.observe(video);
    label(video, state);
  };
  const bindProduct = root => {
    if (products.has(root)) return;
    const overlay = root.querySelector('[data-scp-preview-loop]');
    const viewer = root.querySelector('[data-scdwb-live-model]');
    const load = overlay?.querySelector('[data-scp-load-live]');
    const video = overlay?.querySelector('[data-scp-wheel-loop]');
    if (!overlay || !viewer || !load || !video) return;
    products.add(root);
    viewer.setAttribute('data-scp-video-idle', '');
    viewer.removeAttribute('auto-rotate');
    let requested = false;
    let loadingTimeout;
    const retry = () => {
      if (!requested) return;
      clearTimeout(loadingTimeout);
      requested = false;
      load.disabled = false;
      load.textContent = load.dataset.scpRetryLabel;
    };
    const reveal = () => {
      if (!requested || !viewer.loaded || root.dataset.previewReadyState !== root.dataset.wheelState) return;
      clearTimeout(loadingTimeout);
      overlay.hidden = true;
      video.pause();
      viewer.removeAttribute('data-scp-video-idle');
      viewer.dispatchEvent(new Event('scp:preview-activate'));
      load.disabled = false;
    };
    root.addEventListener('scdwb:preview-ready', reveal);
    load.addEventListener('click', () => {
      requested = true;
      load.disabled = true;
      load.textContent = load.dataset.scpLoadingLabel;
      loadingTimeout = setTimeout(retry, 45000);
      customElements.whenDefined('model-viewer').then(reveal);
    });
    viewer.addEventListener('error', retry);
  };
  const scan = node => {
    if (node.matches?.('[data-scp-wheel-loop]')) bindVideo(node);
    node.querySelectorAll?.('[data-scp-wheel-loop]').forEach(bindVideo);
    if (node.matches?.('scd-wheel-builder')) bindProduct(node);
    node.querySelectorAll?.('scd-wheel-builder').forEach(bindProduct);
  };
  const refresh = () => videos.forEach((state, video) => play(video, state));
  motion.addEventListener('change', refresh);
  document.addEventListener('visibilitychange', refresh);
  new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') {
        const state = videos.get(record.target);
        if (state) { state.paused = false; state.manual = false; play(record.target, state); }
      } else for (const node of record.addedNodes) if (node.nodeType === 1) scan(node);
    }
    for (const [video] of videos) if (!video.isConnected) { video.pause(); observer.unobserve(video); videos.delete(video); }
  }).observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['data-scp-video-profile']});
  scan(document);
})();

